/// <reference types="vite/client" />

import '@/css/app.css';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { createApp } from 'vue';
import PrimeVue from 'primevue/config';

import type { App as VueApp, DefineComponent, Plugin } from 'vue';
import { init, browserTracingIntegration, replayIntegration, setContext } from '@sentry/vue';
import '@/assets/sass/metrica/app.scss';
import '@/assets/sass/eventbooks/app.scss';
import '@/assets/sass/eventbooks/minimal.scss';
import InstantSearch from 'vue-instantsearch/vue3/es';
import App from '@/js/components/App.vue';
import TwoFactorAuthenticate from '@/js/components/TwoFactorAuthenticate.vue';
import { createPinia } from 'pinia';
import { Eventbus } from '@/js/utils/eventbus.ts';
import FloatingVue from 'floating-vue';
import { localise, Auth, isInertiaPage, route } from '@/js/utils.js';
import posthogPlugin from '@/js/plugins/posthog.js';
import Swal from 'sweetalert2';
import { axios } from '@/js/utils/axios.ts';
import { DateTime, Interval } from 'luxon';

import GlobalNotifications from '@/assets/js/eventbooks/components/utility/global-notifications.vue';

import InviteCode from '@/assets/js/eventbooks/components/utility/forms/invite-code.vue';
import Activity from '@/assets/js/eventbooks/components/utility/activity.vue';
import { useLogs } from '@/js/composables/useLogs.ts';

const { logs } = useLogs();
// Commented out until we can sort URL defaults.
// EW is a literal SPA currently so perhaps this gets done with Inertia where there is a middleware to update the defaults on each request.
// const ziggyResponse = await fetch('/api/ziggy');
// const Ziggy = await ziggyResponse.json();

const pinia = createPinia();

const configureApplication = (app: VueApp): VueApp => {
    if (
        ['production', 'staging'].includes(
            (document.querySelector('meta[name="sentry-environment"]') as HTMLMetaElement)?.content || ''
        )
    ) {
        const propagationTargets =
            (document.querySelector('meta[name="sentry-propagation-targets"]') as HTMLMetaElement)?.content
                ?.split(',')
                .map((r: string) => new RegExp(r)) || [];

        init({
            app: app,
            dsn: (document.querySelector('meta[name="sentry-public-dsn"]') as HTMLMetaElement)?.content || '',
            integrations: [
                browserTracingIntegration(),
                replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                }),
            ],
            environment: (document.querySelector('meta[name="sentry-environment"]') as HTMLMetaElement)?.content || '',
            release: (document.querySelector('meta[name="sentry-release"]') as HTMLMetaElement)?.content || '',
            tracesSampleRate: parseFloat(
                (document.querySelector('meta[name="sentry-sample-rate"]') as HTMLMetaElement)?.content || '1.0'
            ),
            tracePropagationTargets: propagationTargets,
            replaysSessionSampleRate: parseFloat(
                (document.querySelector('meta[name="sentry-replay-sample-rate"]') as HTMLMetaElement)?.content || '0.1'
            ),
            replaysOnErrorSampleRate: 1.0,
            beforeSend(event, hint) {
                // Dont report 422's
                // This DEFINITELY exists
                const ex = hint.originalException as { response?: { status?: number } };
                if (ex?.response?.status === 422) {
                    return null;
                }

                setContext('logs', {
                    logs: logs,
                });

                return event;
            },
        });

        setContext('session', {
            started_at_utc: DateTime.utc().toISO(),
        });
    }

    // TODO :: Eventually remove these and import instead, or use `provide` and `inject` RE best practice.
    app.config.globalProperties.localise = localise;
    window.localise = localise;
    app.config.globalProperties.Auth = Auth;
    app.config.globalProperties.route = route;
    app.config.globalProperties.Interval = Interval;
    app.config.globalProperties.flatpickrConfig = {
        altInput: true,
        altFormat: 'F j, Y',
        dateFormat: 'Y-m-d',
        altInputClass:
            'tw-text-sm tw-border tw-border-gray-300 tw-bg-white tw-w-full tw-px-3.5 tw-py-1.5 tw-rounded-md tw-text-gray-900 tw-transition-all tw-duration-150 placeholder:tw-text-gray-400 hover:tw-border-gray-400 focus:tw-border-gray-600 focus:tw-outline-none',
        locale: {
            firstDayOfWeek: 1,
        },
    };
    app.config.globalProperties.flatpickrMultiConfig = {
        mode: 'range',
        altInput: true,
        altFormat: 'F j, Y',
        dateFormat: 'Y-m-d',
        altInputClass:
            'tw-text-sm tw-border tw-border-gray-300 tw-bg-white tw-w-full tw-px-3.5 tw-py-1.5 tw-rounded-md tw-text-gray-900 tw-transition-all tw-duration-150 placeholder:tw-text-gray-400 hover:tw-border-gray-400 focus:tw-border-gray-600 focus:tw-outline-none',
        locale: {
            firstDayOfWeek: 1,
        },
    };

    window.Eventbus = Eventbus;
    window.swal = Swal;
    window.axios = axios;

    app.component('App', App);
    app.component('global-notifications', GlobalNotifications);
    app.component('form-socialite-invite-code', InviteCode);
    app.component('activity', Activity);

    app.use(pinia)
        .use(InstantSearch as unknown as Plugin)
        .use(FloatingVue, {
            themes: {
                tooltip: {
                    triggers: ['hover', 'focus'],
                    placement: 'top',
                    strategy: 'fixed',
                    distance: 10,
                },
            },
        })
        .use(posthogPlugin)
        //.use(ZiggyVue, Ziggy)
        .use(PrimeVue);

    return app;
};

const mount = document.querySelector<HTMLElement>('#app');
if (!mount) throw new Error('Mount point not found.');

const directInertiaPages: Record<string, string> = {
    'Account/Approvals': 'components/pages/Account/Approvals',
    'Account/Contacts': 'components/pages/Account/Contacts',
    'Account/Dashboard': 'components/pages/Account/Dashboard',
    'Account/Departments': 'components/pages/Account/Departments',
    'Account/Entries': 'components/pages/Account/Data/Data',
    'Account/Events': 'components/pages/Account/EventComparison',
    'Account/EventwiseAi': 'components/pages/Account/EventwiseAi/InvoiceMatching',
    'Account/Integrations': 'components/pages/Account/Integrations',
    'Account/Intelligence': 'components/pages/Account/Intelligence',
    'Account/Payruns': 'components/pages/Account/Data/Payruns',
    'Account/Reporting': 'components/pages/Account/Reporting',
    'Account/Settings': 'components/pages/Account/Settings',
    'Account/Team': 'components/pages/Account/Team',
    'Account/Utilisation': 'components/pages/Account/Utilisation/Index',
    'Account/Utilisation/TimeLogs': 'components/pages/Account/Utilisation/TimeLogs',
    'Account/Utilisation/Tasks': 'components/pages/Account/Utilisation/Tasks',
    Admin: 'components/pages/Admin',
    'Event/Approvals': 'components/pages/Approvals',
    'Event/AgencyBudget': 'components/pages/AgencyBudget',
    'Event/Budget': 'components/pages/Budget',
    'Event/BudgetVActuals': 'components/pages/BudgetVActuals',
    'Event/BudgetVActualsSettlementReport': 'components/pages/BudgetVActuals/SettlementReport',
    'Event/Dashboard': 'components/pages/Dashboard',
    'Event/KpiDashboard': 'components/pages/Event/KpiDashboard',
    'Event/Purchasing': 'components/pages/Purchasing',
    'Event/Revenue': 'components/pages/Revenue',
    'Event/ResourcePlan': 'components/pages/ResourcePlan',
    'Event/Settings': 'components/pages/EventSettings',
    'Event/Ticketing': 'components/pages/Ticketing',
    Help: 'components/Help',
    Invitation: 'components/InvitationAccept',
    Onboarding: 'components/Onboarding',
    'User/Me': 'components/pages/Me',
    'User/Notifications': 'components/pages/Notifications',
};

type InertiaPageModule = {
    default?: DefineComponent;
};

const directPageModules = import.meta.glob<InertiaPageModule>([
    '../../../js/components/Help.vue',
    '../../../js/components/InvitationAccept.vue',
    '../../../js/components/Onboarding.vue',
    '../../../js/components/pages/Account/Approvals.vue',
    '../../../js/components/pages/Account/Contacts.vue',
    '../../../js/components/pages/Account/Dashboard.vue',
    '../../../js/components/pages/Account/Data/Data.vue',
    '../../../js/components/pages/Account/Data/Payruns.vue',
    '../../../js/components/pages/Account/Departments.vue',
    '../../../js/components/pages/Account/EventComparison.vue',
    '../../../js/components/pages/Account/EventwiseAi/InvoiceMatching.vue',
    '../../../js/components/pages/Account/Integrations.vue',
    '../../../js/components/pages/Account/Intelligence.vue',
    '../../../js/components/pages/Account/Reporting.vue',
    '../../../js/components/pages/Account/Settings.vue',
    '../../../js/components/pages/Account/Team.vue',
    '../../../js/components/pages/Account/Utilisation/Index.vue',
    '../../../js/components/pages/Account/Utilisation/TimeLogs.vue',
    '../../../js/components/pages/Account/Utilisation/Tasks.vue',
    '../../../js/components/pages/Admin.vue',
    '../../../js/components/pages/Approvals.vue',
    '../../../js/components/pages/AgencyBudget.vue',
    '../../../js/components/pages/Budget.vue',
    '../../../js/components/pages/BudgetVActuals.vue',
    '../../../js/components/pages/BudgetVActuals/SettlementReport.vue',
    '../../../js/components/pages/Dashboard.vue',
    '../../../js/components/pages/Event/KpiDashboard.vue',
    '../../../js/components/pages/EventSettings.vue',
    '../../../js/components/pages/Me.vue',
    '../../../js/components/pages/Notifications.vue',
    '../../../js/components/pages/Purchasing.vue',
    '../../../js/components/pages/Revenue.vue',
    '../../../js/components/pages/ResourcePlan.vue',
    '../../../js/components/pages/Ticketing.vue',
]);
const legacyPageModules = import.meta.glob<InertiaPageModule>('../../../js/Pages/**/*.vue');

const resolveInertiaPage = async (name: string): Promise<DefineComponent> => {
    const directPage = directInertiaPages[name];
    const path = directPage ? `../../../js/${directPage}.vue` : `../../../js/Pages/${name}.vue`;
    const loader = directPageModules[path] ?? legacyPageModules[path];

    if (!loader) {
        throw new Error(`Page not found: ${name}`);
    }

    const module = await loader();

    if (!module.default) {
        throw new Error(`Page module has no default export: ${name}`);
    }

    return module.default;
};

const mfaRequired = mount.dataset.mfa === 'true';
const shouldStartInertia = isInertiaPage();

if (shouldStartInertia) {
    router.on('navigate', () => {
        Eventbus.$emit('routeChanged');
        Eventbus.$emit('pageChanged');
    });
}

if (mfaRequired) {
    configureApplication(createApp(TwoFactorAuthenticate)).mount(mount);
} else if (shouldStartInertia) {
    void createInertiaApp({
        resolve: resolveInertiaPage,
        withApp(app: VueApp) {
            configureApplication(app);
        },
    });
} else {
    configureApplication(createApp(App)).mount(mount);
}
